import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { isNavPathActive, isSectionGroupActive, MenuItemBase } from "./navMenu";

const POPOVER_CLOSE_DELAY_MS = 140;

const navItemSx = {
  minHeight: 34,
  mb: 0.125,
  borderRadius: 1,
  "&.Mui-selected": {
    backgroundColor: "rgba(0, 102, 178, 0.08)",
    "&:hover": {
      backgroundColor: "rgba(0, 102, 178, 0.12)",
    },
  },
};

interface SidebarNavItemProps {
  item: MenuItemBase;
  selected: boolean;
  showText: boolean;
  indent?: boolean;
  onClick: () => void;
}

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  item,
  selected,
  showText,
  indent = false,
  onClick,
}) => (
  <ListItem
    button
    onClick={onClick}
    selected={selected}
    title={item.text}
    sx={{
      ...navItemSx,
      width: "auto",
      minWidth: 0,
      maxWidth: "100%",
      justifyContent: showText ? "initial" : "center",
      px: 1.25,
      mx: showText && indent ? 0.5 : 0.75,
      pl: showText && indent ? 2 : 1.25,
    }}
  >
    <ListItemIcon
      sx={{
        minWidth: 0,
        mr: showText ? 1.25 : "auto",
        justifyContent: "center",
        color: selected ? "primary.main" : "inherit",
        "& .MuiSvgIcon-root": {
          fontSize: "1rem",
        },
      }}
    >
      {item.icon}
    </ListItemIcon>
    {showText && (
      <ListItemText
        primary={item.text}
        sx={{
          minWidth: 0,
          overflow: "hidden",
          "& .MuiListItemText-primary": {
            fontSize: "0.75rem",
            fontWeight: selected ? 500 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
      />
    )}
  </ListItem>
);

interface SidebarNavSectionProps {
  label: MenuItemBase;
  items: MenuItemBase[];
  sidebarOpen: boolean;
  pathname: string;
  onNavigate: (path: string) => void;
}

const SidebarNavSection: React.FC<SidebarNavSectionProps> = ({
  label,
  items,
  sidebarOpen,
  pathname,
  onNavigate,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<number | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const isActive = isSectionGroupActive(label, items, pathname);
  const showInlineItems = expanded && sidebarOpen;
  const popoverOpen = Boolean(anchorEl) && !showInlineItems;

  useEffect(() => {
    return () => {
      if (closeTimer.current != null) {
        window.clearTimeout(closeTimer.current);
      }
    };
  }, []);

  const cancelClose = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openPopover = (el: HTMLElement) => {
    if (showInlineItems) return;
    cancelClose();
    setAnchorEl(el);
  };

  const scheduleClosePopover = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      setAnchorEl(null);
    }, POPOVER_CLOSE_DELAY_MS);
  };

  const toggleExpanded = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setExpanded((current) => !current);
    setAnchorEl(null);
  };

  const openHub = () => {
    if (label.path) onNavigate(label.path);
    setAnchorEl(null);
  };

  const goTo = (path: string) => {
    setAnchorEl(null);
    onNavigate(path);
  };

  return (
    <Box
      onMouseEnter={() => {
        if (headerRef.current) openPopover(headerRef.current);
      }}
      onMouseLeave={scheduleClosePopover}
      sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}
    >
      <Box
        ref={headerRef}
        sx={{
          display: "flex",
          alignItems: "center",
          minHeight: 28,
          minWidth: 0,
          width: "100%",
          boxSizing: "border-box",
          px: sidebarOpen ? 1.25 : 0,
          mt: 0.5,
          borderRadius: 1,
          cursor: "pointer",
          backgroundColor: isActive ? "rgba(0, 102, 178, 0.08)" : "transparent",
          "&:hover": {
            backgroundColor: "rgba(0, 102, 178, 0.12)",
          },
        }}
      >
        {sidebarOpen ? (
          <>
            <Box
              role="button"
              tabIndex={0}
              onClick={openHub}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openHub();
                }
              }}
              title={label.path ? `Open ${label.text}` : undefined}
              sx={{
                flexGrow: 1,
                minWidth: 0,
                overflow: "hidden",
                py: 0.5,
                px: 0.25,
              }}
            >
              <Typography
                className="section-label"
                variant="caption"
                sx={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "0.65rem",
                  fontWeight: isActive ? 700 : 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: isActive ? "primary.main" : "text.secondary",
                  opacity: isActive ? 1 : 0.7,
                }}
              >
                {label.text}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label={
                expanded ? `Collapse ${label.text}` : `Expand ${label.text}`
              }
              onClick={toggleExpanded}
              sx={{
                p: 0.25,
                flexShrink: 0,
                color: isActive ? "primary.main" : "text.secondary",
                "& .MuiSvgIcon-root": {
                  fontSize: "1.05rem",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.15s",
                },
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </>
        ) : (
          <Box
            onClick={openHub}
            title={label.text}
            sx={{
              width: "100%",
              py: 0.75,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: "0.7rem",
                color: isActive ? "primary.main" : "text.secondary",
              }}
            >
              {label.text.charAt(0)}
            </Typography>
          </Box>
        )}
      </Box>

      <Collapse
        in={showInlineItems}
        unmountOnExit
        sx={{ minWidth: 0, maxWidth: "100%" }}
      >
        {items.map(
          (item) =>
            item.path && (
              <SidebarNavItem
                key={item.text}
                item={item}
                selected={isNavPathActive(pathname, item.path)}
                showText
                indent
                onClick={() => goTo(item.path!)}
              />
            ),
        )}
      </Collapse>

      <Popper
        open={popoverOpen}
        anchorEl={anchorEl}
        placement="right-start"
        modifiers={[
          {
            name: "offset",
            options: { offset: [0, 6] },
          },
        ]}
        sx={{ zIndex: 1400 }}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClosePopover}
      >
        <Paper
          elevation={8}
          sx={{
            minWidth: 200,
            py: 0.5,
            borderRadius: 1.5,
          }}
        >
          <Box
            onClick={() => label.path && goTo(label.path)}
            sx={{
              px: 1.5,
              py: 0.75,
              cursor: label.path ? "pointer" : "default",
              "&:hover": label.path
                ? { backgroundColor: "rgba(0, 102, 178, 0.08)" }
                : undefined,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "text.secondary",
              }}
            >
              {label.text}
            </Typography>
          </Box>
          <List dense disablePadding>
            {items.map(
              (item) =>
                item.path && (
                  <SidebarNavItem
                    key={item.text}
                    item={item}
                    selected={isNavPathActive(pathname, item.path)}
                    showText
                    onClick={() => goTo(item.path!)}
                  />
                ),
            )}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
};

export default SidebarNavSection;
export { SidebarNavItem };
